import { UserCredentials } from "../../../store/reducer/slices/userReducer";
import { UserRole, setCredentials } from "./credentials";
import axios from "../../axios";
import toast from "react-hot-toast";

export const URL = "/v2/authentication";

export type LoginCredentials = {
  email: string;
  password: string;
};

export type RegisterCredentials = {
  username: string;
  company_name?: string;
  company_id?: number;
} & LoginCredentials;

type UserAPIResponse = {
  data: {
    username: string;
    user_id: number;
    role: number;
    accessToken: string;
    refreshToken: string;
    pfp: string;
  };
  message?: string;
};

/**
 * Convert a given user object from an API response to match
 * the UserCredentials type used in this application
 */
const parseCredentials = (
  userEmail: string,
  data: any
): UserCredentials => ({
  userId: data?.user_id,
  username: data?.username,
  userRole: data?.role,
  userEmail: userEmail,
  accessToken: data?.accessToken,
  refreshToken: data?.refreshToken
});

/**
 * Make a request to the authentication server to
 * register a new user with the given credentials.
 *
 * @returns Returns `true` if the registration was successful,
 * otherwise returns `undefined`
 */
export const register = async ({
  role,
  email,
  username,
  password,
  company_name,
  company_id
}: {
  role: UserRole.RESEARCHER | UserRole.BUSINESS;
  email: string;
  username: string;
  password: string;
  company_name?: string;
  company_id?: number;
}) => {
  try {
    await axios.post(
      "/v2/register",
      JSON.stringify({
        role,
        email,
        username,
        password,
        ...(company_name ? { company_name } : {}),
        ...(company_id ? { company_id } : {})
      }),
      {
        headers: { "Content-Type": "Application/json" },
        withCredentials: false
      }
    );

    return true;
  } catch (e: any) {
    console.log("in catch", e);
    if (e?.response?.data?.message) {
      toast.error(e.response.data.message);
    }
    return undefined;
  }
};

/**
 * Make a login request to the authentication server,
 * updating the local user credentials if successful.
 *
 * @returns Returns the user credentials if successful,
 * or an object with just the email if 2FA is required.
 */
export const postLogin = async ({ email, password }: LoginCredentials) => {
  const response = await axios.post(
    "/v2/login",
    JSON.stringify({
      email,
      password
    }),
    {
      headers: { "Content-Type": "Application/json" },
      validateStatus: (status) => status >= 200 && status < 300
    }
  );

  // Case: OTP Required (201 Created)
  if (response.status === 201) {
    // Only save the email and mfa_type for OTP/TOTP verification page logic
    setCredentials({ userEmail: email, mfaType: response.data?.mfa_type });
    return { userEmail: email, mfaType: response.data?.mfa_type };
  }

  // Case: Full Login Success (200 OK)
  const credentials = parseCredentials(email, response.data);
  setCredentials(credentials);
  return credentials;
};

/**
 * Google SSO login using an ID token from Google Identity Services.
 */
export const postGoogleLogin = async (idToken: string) => {
  try {
    const response = await axios.post(
      "/v2/login/google",
      JSON.stringify({ id_token: idToken }),
      {
        headers: { "Content-Type": "Application/json" },
        withCredentials: false
      }
    );

    // The response structure is directly in response.data, not response.data.data
    const userData = response.data;

    if (userData.accessToken && userData.user_id) {
      // For Google login, we need to get the email from the backend response
      // The backend should include email in the response, but if not, we'll use username as fallback
      const userEmail = userData.email || userData.username || "";
      const credentials = parseCredentials(userEmail, userData);
      setCredentials(credentials);
      toast.success("Successfully signed in with Google!");
      return credentials;
    } else {
      toast.error("Invalid response from server");
      return undefined;
    }
  } catch (error: any) {
    console.error("Google login error:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Google login failed. Please try again.");
    }
    return undefined;
  }
};

/**
 * Sends an OTP code to the authentication server
 * to get verified - completing the login process
 * if successful.
 *
 * @returns Returns `true` if the code was valid,
 * `false` if the code was invalid. If too many attempts
 * were made (or there was an error validating the OTP)
 * then the user is logged out and returned to the landing
 * page.
 */
export const requestOTPVerfication = async (email: string, code: string, useFallback?: boolean) => {
  try {
    const response = await axios.post(
      "/verify",
      JSON.stringify({
        code,
        email,
        useFallback: !!useFallback
      }),
      {
        headers: { "Content-Type": "Application/json" },
        withCredentials: false
      }
    );

    // Successful response == valid code. User can be logged in
    const credentials = parseCredentials(email, response.data);
    setCredentials(credentials);

    return credentials;
  } catch (error: any) {
    console.error(error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Invalid code. Please try again.");
    }
    throw error;
  }
};

export const requestPasswordReset = async (identity: string) => {
  await axios.post(
    "/sendPasswordReset",
    JSON.stringify({
      email: identity
    }),
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: false
    }
  );
};

export const submitPasswordReset = async (
  password: string,
  email: string,
  token: string
) => {
  await axios.post(
    "submitPasswordToken",
    JSON.stringify({
      newPassword: password,
      email,
      token
    }),
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: false
    }
  );
};

/**
 * Microsoft SSO login using an access token from Microsoft Identity Services.
 */
export const postMicrosoftLogin = async (idToken: string) => {
  try {
    const response = await axios.post(
      "/v2/login/microsoft",
      JSON.stringify({ id_token: idToken, access_token: idToken }),
      {
        headers: { "Content-Type": "Application/json" },
        withCredentials: false
      }
    );

    const userData = response.data;

    if (userData.accessToken && userData.user_id) {
      const userEmail = userData.email || userData.username || "";
      const credentials = parseCredentials(userEmail, userData);
      // Only update localStorage here; Redux will be handled by LoginForm
      setCredentials(credentials);
      return credentials;
    } else {
      toast.error("Invalid response from server");
      return undefined;
    }
  } catch (error: any) {
    console.error("Microsoft login error:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Microsoft login failed. Please try again.");
    }
    return undefined;
  }
};
